import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, CircularProgress, Grid, Link, Paper, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getTalkRoomsByNurseAndPatient } from '../api/talkRooms';
import { Nurse } from '../types/nurse';
import { Patient } from '../types/patient';
import { TALK_ROOM_THEME, TalkRoom, TalkRoomSummaryResponse } from '../types/talk';
import NurseCard from '../components/talkRoom/NurseCard';
import PatientCard from '../components/talkRoom/PatientCard';
import TalkRoomCard from '../components/talkRoom/TalkRoomCard';
import { ChatMessageDownloadButton, ExternalLinkButton } from '../components/common';
import { getExternalTalkRoomUrl } from '../utils/urlUtils';
import { useAuth } from '../hooks/useAuth';
import { DEVELOPER_THEME } from '../types/common';

const TalkRoomSummary: React.FC = () => {
  const { isDeveloper } = useAuth();
  // クエリパラメータを取得
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const nurseIdParam = params.get('nurse_id');
  const patientIdParam = params.get('patient_id');
  const nurseId = nurseIdParam ? Number(nurseIdParam) : 0;
  const patientId = patientIdParam ? Number(patientIdParam) : 0;
  const [ summaryData, setSummaryData ] = useState<TalkRoomSummaryResponse | null>(null);
  const [ talkRooms, setTalkRooms ] = useState<TalkRoom[]>([]);
  const [ nurse, setNurse ] = useState<Nurse | null>(null);
  const [ patient, setPatient ] = useState<Patient | null>(null);
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState<string | null>(null);
  // データ取得
  useEffect(() => {
    // データ取得フラグ
    let isFetching = false;
    let isMounted = true;
    const fetchData = async() => {
      // すでに読み込み中の場合は何もしない
      if (isFetching) return;
      // フラグを立てて重複リクエストを防止
      isFetching = true;
      setLoading(true);
      setError(null);
      // URLにパラメータがない場合
      if (nurseId <= 0 || patientId <= 0) {
        // エラーメッセージを示すが、読み込み中は解除する
        if (isMounted) {
          setError('看護師IDと患者IDを指定してください。対話一覧から選択するか、URLに直接指定してください。');
          setTalkRooms([]);
          setLoading(false);
        }
        isFetching = false;
        return;
      }
      try {
        const response = await getTalkRoomsByNurseAndPatient(nurseId, patientId);
        // コンポーネントがまだマウントされていれば状態を更新
        if (isMounted) {
          setSummaryData(response);
          if (response && response.talk_rooms && response.talk_rooms.length > 0) {
            // IDの降順でソート（新しい順）
            const sortedRooms = [ ...response.talk_rooms ].sort((a, b) => b.id - a.id);
            setTalkRooms(sortedRooms);
            setNurse(response.nurse);
            setPatient(response.patient);
          } else {
            setTalkRooms([]);
            setNurse(null);
            setPatient(null);
          }
        }
      } catch (err) {
        console.error('対話詳細の取得に失敗しました', err);
        if (isMounted) {
          setError('対話データの取得に失敗しました。API接続を確認してください。');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
        isFetching = false;
      }
    };
    // 即時実行せず、わずかな遅延を入れて実行（状態変更の安定化のため）
    const timerId = setTimeout(() => {
      fetchData();
    }, 10);
    // クリーンアップ関数
    return () => {
      isMounted = false;
      clearTimeout(timerId);
    };
  }, [ nurseId, patientId ]);
  const clearSessionStorage = () => {
    sessionStorage.removeItem('talkRoomSummary_nurseId');
    sessionStorage.removeItem('talkRoomSummary_patientId');
  };
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress/>
        <Typography sx={{ mt: 2 }}>対話データを読み込んでいます...</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ maxWidth: '800px', mx: 'auto', mt: 4, p: 2 }}>
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Link component={RouterLink} to="/talk_rooms" color="inherit" sx={{ textDecoration: 'none' }}>
              <Button
                color="inherit"
                size="small"
                onClick={clearSessionStorage}
              >
                対話一覧へ戻る
              </Button>
            </Link>
          }
        >
          <AlertTitle>ご注意</AlertTitle>
          {error}
        </Alert>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            対話サマリーを表示するには
          </Typography>
          <Typography variant="body1" paragraph>
            対話一覧から対話を選択すると、看護師と患者の間の全ての対話が表示されます。
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/talk_rooms"
            startIcon={<ArrowBackIcon/>}
            sx={{ mt: 2 }}
            onClick={clearSessionStorage}
          >
            対話一覧へ戻る
          </Button>
        </Paper>
      </Box>
    );
  }
  if (talkRooms.length === 0) {
    return (
      <Box sx={{ maxWidth: '800px', mx: 'auto', mt: 4, p: 2 }}>
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Link component={RouterLink} to="/talk_rooms" color="inherit" sx={{ textDecoration: 'none' }}>
              <Button
                color="inherit"
                size="small"
                onClick={clearSessionStorage}
              >
                対話一覧へ戻る
              </Button>
            </Link>
          }
        >
          <AlertTitle>情報</AlertTitle>
          看護師ID: {nurseId}と患者ID: {patientId}の間の対話データが見つかりませんでした。
        </Alert>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" paragraph>
            指定された看護師と患者の間に対話履歴がないか、対話データが削除された可能性があります。
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/talk_rooms"
            startIcon={<ArrowBackIcon/>}
            sx={{ mt: 2 }}
            onClick={clearSessionStorage}
          >
            対話一覧へ戻る
          </Button>
        </Paper>
      </Box>
    );
  }
  // 看護師と患者の情報はステートから取得
  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon/>}
          component={RouterLink}
          to="/talk_rooms"
          onClick={clearSessionStorage}
        >
          一覧に戻る
        </Button>
      </Box>

      {/* 看護師と患者の情報をページ上部に表示 */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <NurseCard nurse={nurse}/>
          </Grid>

          <Grid item xs={12} md={6}>
            <PatientCard patient={patient}/>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
          対話履歴
        </Typography>
        {talkRooms.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <ExternalLinkButton
              href={getExternalTalkRoomUrl(talkRooms[0].id)}
              size="small"
              variant="outlined"
              color="primary"
              buttonType="chat"
              themeColor={{
                main: TALK_ROOM_THEME.DARK,
                hover: `${TALK_ROOM_THEME.BACKGROUND}99`
              }}
            />
            <ChatMessageDownloadButton
              roomType="TALK_ROOM"
              roomId={talkRooms[0].id} // 最新の対話ルームID（配列の最初）
              size="small"
              variant="outlined"
              color="primary"
              buttonText="ダウンロード"
            />
            {isDeveloper() && (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: DEVELOPER_THEME.BACKGROUND_ALPHA,
                  border: `1px dashed ${DEVELOPER_THEME.BORDER_ALPHA}`,
                  color: DEVELOPER_THEME.DARK,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.85rem'
                }}
              >
                ChatroomID: {talkRooms[0].chatroom_id}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {talkRooms.map((room) => (
        <TalkRoomCard key={room.id} room={room} nurse={nurse} patient={patient}/>
      ))}
    </Box>
  );
};
export default TalkRoomSummary;
